/**
 * ShareButtons — reusable share row used across BillingPage, OrdersPage, DuesPage.
 *
 * Props:
 *   phone        {string}   – customer mobile (digits only, 10-digit Indian)
 *   text         {string}   – pre-built WhatsApp / SMS message text
 *   getShareUrl  {function} – optional async () => string; if provided, appends the URL to text
 *   onPrint      {function} – optional () => void; if provided, shows Print button
 *   noPhone      {boolean}  – the customer has no phone: WhatsApp and SMS are greyed out with a
 *                             note (Print / View stay), so a bill is never sent to nobody
 *   size         {'sm'|'md'} – icon button size, default 'md'
 */

import React, { useState } from 'react';
import { Box, IconButton, Tooltip, CircularProgress, Typography } from '@mui/material';
import { WhatsApp, Sms, Print, Visibility } from '@mui/icons-material';

const BTN = {
    wa:    { title: 'WhatsApp',    bg: '#25D366', hover: '#1ebe5d' },
    wab:   { title: 'WA Business', bg: '#128C7E', hover: '#075E54' },
    sms:   { title: 'SMS',        bg: '#2563EB', hover: '#1E3A8A' },
    print: { title: 'Print',      bg: '#555',    hover: '#333'    },
    view:  { title: 'View Invoice', bg: '#0288d1', hover: '#0277bd' },
};

function iconBtnSx(key, sizePx) {
    const { bg, hover } = BTN[key];
    return {
        bgcolor: bg,
        color: '#fff',
        '&:hover': { bgcolor: hover },
        width: sizePx,
        height: sizePx,
    };
}

export default function ShareButtons({ phone, text, getShareUrl, onPrint, onView, size = 'md', label = true, noPhone = false }) {
    // Greyed, not hidden: the vendor sees why there's no WhatsApp for this customer.
    const offSx = noPhone ? { opacity: 0.35 } : {};
    const [loading, setLoading] = useState({ wa: false, wab: false, sms: false, print: false, view: false });
    const sizePx = size === 'sm' ? 28 : 40;
    const iconSx = size === 'sm' ? { fontSize: 14 } : { fontSize: 'small' };
    const spinSize = size === 'sm' ? 12 : 18;
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    const waPhone = (() => {
        const digits = (phone || '').replace(/\D/g, '');
        return digits.length === 10 ? `91${digits}` : digits;
    })();

    const buildMessage = async () => {
        if (getShareUrl) {
            try {
                const url = await getShareUrl();
                return url ? `${text}\n\n${url}` : text;
            } catch {
                return text;
            }
        }
        return text;
    };

    const handleWa = async () => {
        setLoading(p => ({ ...p, wa: true }));
        try {
            const msg = await buildMessage();
            if (isMobile) {
                // Use deep link to open native WhatsApp app on Android/iOS
                const a = document.createElement('a');
                a.href = `whatsapp://send?phone=${waPhone}&text=${encodeURIComponent(msg)}`;
                a.click();
            } else {
                const win = window.open('', '_blank');
                const dest = `https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`;
                if (win) win.location.href = dest; else window.location.href = dest;
            }
        } catch {
            // silent
        } finally {
            setLoading(p => ({ ...p, wa: false }));
        }
    };

    const handleWab = async () => {
        setLoading(p => ({ ...p, wab: true }));
        try {
            const msg = await buildMessage();
            if (isMobile) {
                // Use deep link to open native WhatsApp Business app on Android/iOS
                const a = document.createElement('a');
                a.href = `whatsapp://send?phone=${waPhone}&text=${encodeURIComponent(msg)}`;
                a.click();
            } else {
                const win = window.open('', '_blank');
                const dest = `https://api.whatsapp.com/send?phone=${waPhone}&text=${encodeURIComponent(msg)}`;
                if (win) win.location.href = dest; else window.location.href = dest;
            }
        } catch {
            // silent
        } finally {
            setLoading(p => ({ ...p, wab: false }));
        }
    };

    const handlePrint = async () => {
        if (!onPrint) return;
        setLoading(p => ({ ...p, print: true }));
        try { await onPrint(); } finally {
            setLoading(p => ({ ...p, print: false }));
        }
    };

    const handleView = async () => {
        if (!onView) return;
        setLoading(p => ({ ...p, view: true }));
        try {
            await onView();
        } catch {
            // silent
        } finally {
            setLoading(p => ({ ...p, view: false }));
        }
    };

    const handleSms = async () => {
        setLoading(p => ({ ...p, sms: true }));
        try {
            const msg = await buildMessage();
            const href = `sms:${waPhone ? `+${waPhone}` : ''}?body=${encodeURIComponent(msg)}`;
            const a = document.createElement('a');
            a.href = href;
            a.click();
        } catch {
            // silent
        } finally {
            setLoading(p => ({ ...p, sms: false }));
        }
    };

    return (
        <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider', p: size === 'sm' ? 0.75 : 1.5 }}>
            {label && (
                <Typography variant="caption" color="text.secondary" fontWeight="bold"
                    sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}>
                    Share via
                </Typography>
            )}
            <Box sx={{ display: 'flex', gap: size === 'sm' ? 0.5 : 0.75, justifyContent: 'center', flexWrap: 'wrap' }}>
                {/* WhatsApp */}
                <Tooltip title={BTN.wa.title}>
                    <IconButton onClick={handleWa} disabled={noPhone || loading.wa} sx={{ ...iconBtnSx('wa', sizePx), ...offSx }}>
                        {loading.wa ? <CircularProgress size={spinSize} color="inherit" /> : <WhatsApp sx={iconSx} />}
                    </IconButton>
                </Tooltip>

                {/* WA Business */}
                <Tooltip title={BTN.wab.title}>
                    <IconButton onClick={handleWab} disabled={noPhone || loading.wab} sx={{ ...iconBtnSx('wab', sizePx), ...offSx }}>
                        {loading.wab ? <CircularProgress size={spinSize} color="inherit" /> : <WhatsApp sx={iconSx} />}
                    </IconButton>
                </Tooltip>

                {/* SMS */}
                <Tooltip title={BTN.sms.title}>
                    <IconButton onClick={handleSms} disabled={noPhone || loading.sms} sx={{ ...iconBtnSx('sms', sizePx), ...offSx }}>
                        {loading.sms ? <CircularProgress size={spinSize} color="inherit" /> : <Sms sx={iconSx} />}
                    </IconButton>
                </Tooltip>

                {/* Print (optional) */}
                {onPrint && (
                    <Tooltip title={BTN.print.title}>
                        <IconButton onClick={handlePrint} disabled={loading.print} sx={iconBtnSx('print', sizePx)}>
                            {loading.print ? <CircularProgress size={spinSize} color="inherit" /> : <Print sx={iconSx} />}
                        </IconButton>
                    </Tooltip>
                )}

                {/* View Invoice (optional) */}
                {onView && (
                    <Tooltip title={BTN.view.title}>
                        <IconButton onClick={handleView} disabled={loading.view} sx={iconBtnSx('view', sizePx)}>
                            {loading.view ? <CircularProgress size={spinSize} color="inherit" /> : <Visibility sx={iconSx} />}
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            {noPhone && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.75 }}>
                    No phone — print the bill or hand it over
                </Typography>
            )}
        </Box>
    );
}
